import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    message,
    Popconfirm,
    Card,
    Row,
    Col,
    Tooltip,
    Tag
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';
import {
    FirmwareVersion,
    FirmwareVersionListRequest,
    CreateFirmwareVersionRequest,
    UpdateFirmwareVersionRequest,
    BatchCreateFirmwareVersionRequest,
    BatchCreateFirmwareVersionResponse,
    App,
    DeviceType
} from '../types';

const { TextArea } = Input;
const { Option } = Select;

const FirmwareVersionManagement: React.FC = () => {
    const [firmwareVersions, setFirmwareVersions] = useState<FirmwareVersion[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 20;

    // 筛选状态
    const [filterAppId, setFilterAppId] = useState<number | undefined>(undefined);
    const [filterDeviceTypeId, setFilterDeviceTypeId] = useState<number | undefined>(undefined);

    // 下拉选项数据
    const [apps, setApps] = useState<App[]>([]);
    const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);

    // 模态框状态
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingFirmwareVersion, setEditingFirmwareVersion] = useState<FirmwareVersion | null>(null);
    const [form] = Form.useForm();

    // 批量添加模态框状态
    const [isBatchModalVisible, setIsBatchModalVisible] = useState<boolean>(false);
    const [batchForm] = Form.useForm();
    const [batchLoading, setBatchLoading] = useState<boolean>(false);

    // 获取应用列表
    const fetchApps = async () => {
        try {
            const response = await ApiService.getAppList({ page: 1, page_size: 50 });
            if (response.code === 200) {
                setApps(response.data.apps);
            }
        } catch (error) {
            console.error('获取应用列表失败:', error);
        }
    };

    // 获取设备类型列表
    const fetchDeviceTypes = async () => {
        try {
            const response = await ApiService.getDeviceTypeList({ page: 1, page_size: 50 });
            if (response.code === 200) {
                setDeviceTypes(response.data.device_types);
            }
        } catch (error) {
            console.error('获取设备类型列表失败:', error);
        }
    };

    // 获取固件版本列表
    const fetchFirmwareVersions = async (page: number = currentPage) => {
        setLoading(true);
        try {
            const request: FirmwareVersionListRequest = {
                page,
                page_size: pageSize,
                app_id: filterAppId,
                device_type_id: filterDeviceTypeId
            };
            const response = await ApiService.getFirmwareVersionList(request);
            if (response.code === 200) {
                setFirmwareVersions(response.data.firmware_versions);
                setTotal(response.data.total);
                setCurrentPage(page);
            } else {
                message.error(response.message || '获取固件版本列表失败');
            }
        } catch (error) {
            console.error('获取固件版本列表失败:', error);
            message.error('获取固件版本列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 页面加载时获取数据
    useEffect(() => {
        fetchApps();
        fetchDeviceTypes();
    }, []);

    useEffect(() => {
        fetchFirmwareVersions(1);
    }, [filterAppId, filterDeviceTypeId]);

    // 处理新增固件版本
    const handleAdd = () => {
        setEditingFirmwareVersion(null);
        setIsModalVisible(true);
        form.resetFields();
        form.setFieldsValue({
            is_latest: false,
            ota_ver: 0
        });
    };

    // 处理编辑固件版本
    const handleEdit = (firmwareVersion: FirmwareVersion) => {
        setEditingFirmwareVersion(firmwareVersion);
        setIsModalVisible(true);
        form.setFieldsValue({
            app_id: firmwareVersion.app_id,
            device_type_id: firmwareVersion.device_type_id,
            firmware_version: firmwareVersion.firmware_version,
            download_url: firmwareVersion.download_url,
            is_latest: firmwareVersion.is_latest,
            ota_ver: firmwareVersion.ota_ver ?? 0,
            release_notes: firmwareVersion.release_notes,
            remark: firmwareVersion.remark
        });
    };

    // 处理删除固件版本
    const handleDelete = async (id: number) => {
        try {
            const response = await ApiService.deleteFirmwareVersion({ id });
            if (response.code === 200) {
                message.success(response.message || '固件版本删除成功');
                fetchFirmwareVersions(currentPage);
            } else {
                message.error(response.message || '固件版本删除失败');
            }
        } catch (error) {
            console.error('删除固件版本失败:', error);
            message.error('删除固件版本失败');
        }
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingFirmwareVersion) {
                // 编辑固件版本
                const request: UpdateFirmwareVersionRequest = {
                    id: editingFirmwareVersion.id,
                    app_id: values.app_id,
                    device_type_id: values.device_type_id,
                    firmware_version: values.firmware_version,
                    download_url: values.download_url,
                    is_latest: values.is_latest,
                    ota_ver: typeof values.ota_ver === 'number' ? values.ota_ver : 0,
                    release_notes: values.release_notes || '',
                    remark: values.remark || ''
                };
                const response = await ApiService.updateFirmwareVersion(request);
                if (response.code === 200) {
                    message.success(response.message || '固件版本更新成功');
                    setIsModalVisible(false);
                    fetchFirmwareVersions(currentPage);
                } else {
                    message.error(response.message || '固件版本更新失败');
                }
            } else {
                // 新增固件版本
                const request: CreateFirmwareVersionRequest = {
                    app_id: values.app_id,
                    device_type_id: values.device_type_id,
                    firmware_version: values.firmware_version,
                    download_url: values.download_url,
                    is_latest: values.is_latest,
                    ota_ver: typeof values.ota_ver === 'number' ? values.ota_ver : 0,
                    release_notes: values.release_notes || '',
                    remark: values.remark || ''
                };
                const response = await ApiService.createFirmwareVersion(request);
                if (response.code === 200) {
                    message.success(response.message || '固件版本创建成功');
                    setIsModalVisible(false);
                    fetchFirmwareVersions(1); // 新增后回到第一页
                } else {
                    message.error(response.message || '固件版本创建失败');
                }
            }
        } catch (error) {
            console.error('提交失败:', error);
        }
    };

    // 处理模态框取消
    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingFirmwareVersion(null);
    };

    // 处理批量添加
    const handleBatchAdd = () => {
        setIsBatchModalVisible(true);
        batchForm.resetFields();
        // 设置默认值
        batchForm.setFieldsValue({
            common_is_latest: false,
            common_ota_ver: 0,
            device_downloads: [{}] // 初始化一个空项
        });
    };

    // 处理批量模态框确认
    const handleBatchModalOk = async () => {
        try {
            setBatchLoading(true);
            const values = await batchForm.validateFields();

            // 获取公共信息
            const commonInfo = {
                app_id: values.common_app_id,
                firmware_version: values.common_firmware_version,
                is_latest: values.common_is_latest || false,
                ota_ver: typeof values.common_ota_ver === 'number' ? values.common_ota_ver : 0,
                release_notes: values.common_release_notes || '',
                remark: values.common_remark || ''
            };

            // 过滤掉空的设备信息并组合公共信息
            const firmwareVersions = values.device_downloads
                .filter((item: any) => item && item.device_type_id && item.download_url)
                .map((item: any) => ({
                    ...commonInfo,
                    device_type_id: item.device_type_id,
                    download_url: item.download_url
                }));

            if (firmwareVersions.length === 0) {
                message.warning('请至少添加一个有效的设备下载信息');
                return;
            }

            const request: BatchCreateFirmwareVersionRequest = {
                firmware_versions: firmwareVersions
            };

            const response = await ApiService.batchCreateFirmwareVersion(request);
            if (response.code === 200) {
                const result = response.data;

                // 显示批量操作结果
                Modal.info({
                    title: '批量创建结果',
                    width: 600,
                    content: (
                        <div>
                            <p>总计请求: {result.total_requested} 个</p>
                            <p style={{ color: '#52c41a' }}>成功: {result.success_count} 个</p>
                            <p style={{ color: '#ff4d4f' }}>失败: {result.failed_count} 个</p>

                            {result.failed_count > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <p><strong>失败详情:</strong></p>
                                    <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                        {result.results
                                            .filter(item => !item.success)
                                            .map((item, index) => (
                                                <p key={index} style={{ color: '#ff4d4f', margin: '4px 0' }}>
                                                    第 {item.index + 1} 项: {item.error}
                                                </p>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    ),
                    onOk: () => {
                        fetchFirmwareVersions(1);
                    }
                });

                setIsBatchModalVisible(false);
                batchForm.resetFields();
            } else {
                message.error(response.message || '批量创建失败');
            }
        } catch (error) {
            console.error('批量创建失败:', error);
            message.error('批量创建失败');
        } finally {
            setBatchLoading(false);
        }
    };

    // 处理批量模态框取消
    const handleBatchModalCancel = () => {
        setIsBatchModalVisible(false);
        batchForm.resetFields();
    };

    // 表格列定义
    const columns: ColumnsType<FirmwareVersion> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '应用名称',
            dataIndex: 'app_name',
            key: 'app_name',
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            )
        },
        {
            title: '设备类型',
            dataIndex: 'device_type_description',
            key: 'device_type_description',
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            )
        },
        {
            title: '固件版本',
            dataIndex: 'firmware_version',
            key: 'firmware_version',
            width: 120
        },
        {
            title: 'OTA版本',
            dataIndex: 'ota_ver',
            key: 'ota_ver',
            width: 100,
            render: (ota_ver: number) => (
                <Tag color="blue">{ota_ver ?? 0}</Tag>
            )
        },
        {
            title: '是否最新',
            dataIndex: 'is_latest',
            key: 'is_latest',
            width: 100,
            render: (is_latest: boolean) => (
                <Tag color={is_latest ? 'green' : 'default'}>
                    {is_latest ? '最新' : '非最新'}
                </Tag>
            )
        },
        {
            title: '下载地址',
            dataIndex: 'download_url',
            key: 'download_url',
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    <a href={text} target="_blank" rel="noopener noreferrer">
                        {text}
                    </a>
                </Tooltip>
            )
        },
        {
            title: '发布说明',
            dataIndex: 'release_notes',
            key: 'release_notes',
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text || '-'}
                </Tooltip>
            )
        },
        {
            title: '备注',
            dataIndex: 'remark',
            key: 'remark',
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text || '-'}
                </Tooltip>
            )
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (text: string) => new Date(text).toLocaleString('zh-CN')
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="确定要删除这个固件版本吗？此操作不可恢复。"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <h2 style={{ margin: 0 }}>固件版本管理</h2>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchFirmwareVersions(currentPage)}
                                loading={loading}
                            >
                                刷新
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                            >
                                新增固件版本
                            </Button>
                            <Button
                                type="default"
                                icon={<UploadOutlined />}
                                onClick={handleBatchAdd}
                            >
                                批量添加
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* 筛选条件 */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Select
                            placeholder="选择应用"
                            allowClear
                            style={{ width: '100%' }}
                            value={filterAppId}
                            onChange={setFilterAppId}
                        >
                            {apps.map(app => (
                                <Option key={app.id} value={app.id}>
                                    {app.app_name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={8}>
                        <Select
                            placeholder="选择设备类型"
                            allowClear
                            style={{ width: '100%' }}
                            value={filterDeviceTypeId}
                            onChange={setFilterDeviceTypeId}
                        >
                            {deviceTypes.map(deviceType => (
                                <Option key={deviceType.id} value={deviceType.id}>
                                    {deviceType.description}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={firmwareVersions}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1500 }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                        onChange: (page) => fetchFirmwareVersions(page),
                    }}
                    size="middle"
                />
            </Card>

            <Modal
                title={editingFirmwareVersion ? '编辑固件版本' : '新增固件版本'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="确定"
                cancelText="取消"
                width={800}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="应用"
                                name="app_id"
                                rules={[{ required: true, message: '请选择应用' }]}
                            >
                                <Select placeholder="请选择应用">
                                    {apps.map(app => (
                                        <Option key={app.id} value={app.id}>
                                            {app.app_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="设备类型"
                                name="device_type_id"
                                rules={[{ required: true, message: '请选择设备类型' }]}
                            >
                                <Select placeholder="请选择设备类型">
                                    {deviceTypes.map(deviceType => (
                                        <Option key={deviceType.id} value={deviceType.id}>
                                            {deviceType.description}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="固件版本"
                                name="firmware_version"
                                rules={[
                                    { required: true, message: '请输入固件版本' },
                                    { max: 50, message: '固件版本不能超过50个字符' }
                                ]}
                            >
                                <Input placeholder="请输入固件版本，如：1.0.0" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="是否最新版本"
                                name="is_latest"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="OTA版本号"
                                name="ota_ver"
                                tooltip="用于区分不同 OTA 通道/分组的版本号，默认 0"
                                initialValue={0}
                                rules={[
                                    { required: true, message: '请输入OTA版本号' }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="默认 0"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="下载地址"
                        name="download_url"
                        rules={[
                            { required: true, message: '请输入下载地址' },
                            { type: 'url', message: '请输入有效的URL地址' },
                            { max: 500, message: '下载地址不能超过500个字符' }
                        ]}
                    >
                        <Input placeholder="请输入固件下载地址" />
                    </Form.Item>

                    <Form.Item
                        label="发布说明"
                        name="release_notes"
                    >
                        <TextArea
                            rows={3}
                            placeholder="请输入发布说明"
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        label="备注"
                        name="remark"
                    >
                        <TextArea
                            rows={2}
                            placeholder="请输入备注信息"
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 批量添加模态框 */}
            <Modal
                title="批量添加固件版本"
                open={isBatchModalVisible}
                onOk={handleBatchModalOk}
                onCancel={handleBatchModalCancel}
                width={800}
                confirmLoading={batchLoading}
                okText="批量创建"
                cancelText="取消"
            >
                <Form
                    form={batchForm}
                    layout="vertical"
                >
                    {/* 公共信息区域 */}
                    <Card size="small" title="公共信息" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="应用"
                                    name="common_app_id"
                                    rules={[{ required: true, message: '请选择应用' }]}
                                >
                                    <Select placeholder="请选择应用">
                                        {apps.map(app => (
                                            <Option key={app.id} value={app.id}>
                                                {app.app_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="固件版本"
                                    name="common_firmware_version"
                                    rules={[{ required: true, message: '请输入固件版本' }]}
                                >
                                    <Input placeholder="例如: 1.0.0" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="是否最新版本"
                                    name="common_is_latest"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="OTA版本号"
                                    name="common_ota_ver"
                                    tooltip="用于区分不同 OTA 通道/分组的版本号，默认 0"
                                    initialValue={0}
                                    rules={[
                                        { required: true, message: '请输入OTA版本号' }
                                    ]}
                                >
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        placeholder="默认 0"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="发布说明"
                            name="common_release_notes"
                        >
                            <TextArea
                                rows={3}
                                placeholder="请输入发布说明"
                                maxLength={1000}
                                showCount
                            />
                        </Form.Item>

                        <Form.Item
                            label="备注"
                            name="common_remark"
                        >
                            <TextArea
                                rows={2}
                                placeholder="请输入备注信息"
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </Card>

                    {/* 设备列表区域 */}
                    <Form.List name="device_downloads">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card
                                        key={key}
                                        size="small"
                                        title={`设备 ${name + 1}`}
                                        style={{ marginBottom: 16 }}
                                        extra={
                                            fields.length > 1 ? (
                                                <Button
                                                    type="link"
                                                    danger
                                                    onClick={() => remove(name)}
                                                >
                                                    删除
                                                </Button>
                                            ) : null
                                        }
                                    >
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'device_type_id']}
                                                    label="设备类型"
                                                    rules={[{ required: true, message: '请选择设备类型' }]}
                                                >
                                                    <Select placeholder="请选择设备类型">
                                                        {deviceTypes.map(deviceType => (
                                                            <Option key={deviceType.id} value={deviceType.id}>
                                                                {deviceType.description}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'download_url']}
                                                    label="下载地址"
                                                    rules={[
                                                        { required: true, message: '请输入下载地址' },
                                                        { type: 'url', message: '请输入有效的URL' }
                                                    ]}
                                                >
                                                    <Input placeholder="https://example.com/firmware/v1.0.0.bin" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}

                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                        disabled={fields.length >= 10}
                                    >
                                        添加设备 {fields.length >= 10 && '(最多10个)'}
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </div>
    );
};

export default FirmwareVersionManagement;